import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import StlViewerModal from './StlViewerModal';
import CasePrescriptionReadView from './CasePrescriptionReadView';
import PrescriptionForm from './PrescriptionForm';
import CaseClinicalMediaBoard from './CaseClinicalMediaBoard';
import {
  canEditPrescriptionOnFilesTab,
  canModifyCaseFiles,
} from '../../../lib/caseFileEditPolicy';
import {
  buildTreatmentSpecsPayloadFromCase,
  formSnapshotFromCase,
} from '../../../lib/casePrescriptionPayload';
import {
  fetchSignedUrlsByFileId,
  revokePreviewBlobUrls,
} from '../../../lib/caseFileSignedUrls';
import { listClinicalSlotFiles, listNonSlotFiles } from '../../../lib/caseClinicalFileSlots';
import { ADMIN_ASSISTANT_ROLES } from '../../../lib/dentistSubmissionWizard';
import { isCaseStlFile, toStlViewerFileEntry } from '../../../lib/caseFileDisplayLabels';
import CaseOrderServicesSummary from './CaseOrderServicesSummary';

const BULK_DOWNLOAD_DELAY_MS = 300;

const CLINICAL_CATEGORIES = new Set(['all', 'scans', 'photos']);

const FilesTab = ({
  files,
  onDelete,
  onDownload,
  onFilesUploaded,
  userRole,
  userRoleTypes = [],
  caseData,
  patientId,
  currentUserId,
  timelinePhaseItems = [],
  templateActionsByTemplateId = {},
  onPrescriptionSave,
}) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [previewFile, setPreviewFile] = useState(null);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [signedUrls, setSignedUrls] = useState({});
  const [loadingUrls, setLoadingUrls] = useState(new Set());
  const [stlViewerOpen, setStlViewerOpen] = useState(false);
  const [stlViewerFiles, setStlViewerFiles] = useState([]);
  const [prescriptionData, setPrescriptionData] = useState(null);

  const fileEditCtx = {
    userRole,
    userRoleTypes,
    caseData,
    timelinePhaseItems,
    templateActionsByTemplateId,
  };

  const canModifyFiles = canModifyCaseFiles(fileEditCtx);
  const canEditPrescription = canEditPrescriptionOnFilesTab(fileEditCtx);

  const showClinicalBoard = CLINICAL_CATEGORIES.has(selectedCategory);
  const boardMode =
    selectedCategory === 'scans' || selectedCategory === 'photos' ? selectedCategory : 'all';

  const canBulkDownloadClinical = useMemo(() => {
    const roles = userRoleTypes?.length ? userRoleTypes : userRole ? [userRole] : [];
    return roles.some((r) => ADMIN_ASSISTANT_ROLES.has(r));
  }, [userRole, userRoleTypes]);

  const clinicalDownloadTargets = useMemo(
    () => listClinicalSlotFiles(files || [], boardMode),
    [files, boardMode],
  );

  const orphanFiles = useMemo(() => {
    if (selectedCategory !== 'all') return [];
    return listNonSlotFiles(files || []);
  }, [files, selectedCategory]);

  const imagePreviewPayload = useMemo(() => {
    const targets = orphanFiles
      .filter((f) => f.type === 'image' && (f.filePath || f.thumbnail))
      .map((f) => ({
        id: f.id,
        path: f.filePath || f.thumbnail,
        name: f.name || '',
      }))
      .sort((a, b) => String(a.id).localeCompare(String(b.id)));
    return JSON.stringify(targets);
  }, [orphanFiles]);

  const categories = [
    { value: 'all', label: 'Tous les fichiers', icon: 'FolderOpen' },
    { value: 'scans', label: 'Scans', icon: 'Scan' },
    { value: 'photos', label: 'Photos', icon: 'Image' },
    { value: 'prescription', label: 'Prescription', icon: 'FileText' },
  ];

  useEffect(() => {
    if (imagePreviewPayload === '[]') {
      setSignedUrls({});
      setLoadingUrls(new Set());
      return;
    }

    let cancelled = false;
    const targets = JSON.parse(imagePreviewPayload);

    const run = async () => {
      setLoadingUrls(new Set(targets.map((t) => t.id)));

      try {
        const imageFiles = targets.map((t) => ({
          id: t.id,
          filePath: t.path,
          name: t.name,
        }));
        const next = await fetchSignedUrlsByFileId(imageFiles);

        if (cancelled) {
          revokePreviewBlobUrls(next);
          return;
        }

        setSignedUrls((prev) => {
          revokePreviewBlobUrls(prev);
          return next;
        });
      } catch (error) {
        console.error('Error fetching signed URLs:', error);
      } finally {
        if (!cancelled) setLoadingUrls(new Set());
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [imagePreviewPayload]);

  const handleBulkDownloadClinical = useCallback(async () => {
    if (!clinicalDownloadTargets.length || typeof onDownload !== 'function') return;
    setIsBulkDownloading(true);
    let successCount = 0;
    try {
      for (let i = 0; i < clinicalDownloadTargets.length; i += 1) {
        if (i > 0) {
          await new Promise((resolve) => {
            setTimeout(resolve, BULK_DOWNLOAD_DELAY_MS);
          });
        }
        await onDownload(clinicalDownloadTargets[i].id);
        successCount += 1;
      }
      if (successCount > 0) {
        toast.success(
          successCount === 1
            ? '1 fichier téléchargé'
            : `${successCount} fichiers téléchargés`,
        );
      }
    } catch (error) {
      console.error('Bulk clinical download failed:', error);
    } finally {
      setIsBulkDownloading(false);
    }
  }, [clinicalDownloadTargets, onDownload]);

  const openStlViewer = (clickedFile) => {
    const stlList = orphanFiles.filter((f) => isCaseStlFile(f)).map(toStlViewerFileEntry);
    if (stlList.length === 0) return;
    const clicked = toStlViewerFileEntry(clickedFile);
    const firstPath = clicked.filePath;
    const rest = stlList.filter((f) => f.filePath !== firstPath);
    setStlViewerFiles([clicked, ...rest]);
    setStlViewerOpen(true);
  };

  const getFileIcon = (type) => {
    const icons = {
      pdf: 'FileText',
      image: 'Image',
      scan: 'Scan',
      document: 'File',
    };
    return icons?.[type] || 'File';
  };

  const renderOrphanFileCard = (file) => (
    <div
      key={file?.id}
      className="bg-card border border-border rounded-lg p-4 hover:shadow-interactive transition-shadow"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name={getFileIcon(file?.type)} size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className="text-sm font-medium text-foreground truncate"
              title={file?.technicalName || file?.name}
            >
              {file?.name}
            </h3>
            <p className="text-xs text-muted-foreground">{file?.size}</p>
          </div>
        </div>
      </div>

      {file?.type === 'image' && file?.thumbnail && (
        <div className="mb-3 overflow-hidden rounded-lg h-32 bg-muted">
          {loadingUrls.has(file.id) ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : signedUrls[file.id] ? (
            <Image
              src={signedUrls[file.id]}
              alt={file?.name}
              className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
              onClick={() => setPreviewFile({ ...file, url: signedUrls[file.id] })}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Icon name="Image" size={32} className="text-muted-foreground" />
            </div>
          )}
        </div>
      )}

      <div className="space-y-2 mb-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Téléversé par:</span>
          <span className="text-foreground font-medium">{file?.uploadedBy}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Date:</span>
          <span className="text-foreground font-medium">{file?.uploadDate}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Version:</span>
          <span className="text-foreground font-medium">{file?.version}</span>
        </div>
      </div>

      <div className="flex gap-1.5 items-center min-w-0">
        {isCaseStlFile(file) && (
          <Button
            type="button"
            variant="outline"
            size="xs"
            iconName="Box"
            iconPosition="left"
            className="flex-1 min-w-0 px-1.5 sm:px-2"
            onClick={() => openStlViewer(file)}
          >
            <span className="truncate">Aperçu 3D</span>
          </Button>
        )}
        <Button
          variant="outline"
          size="xs"
          iconName="Download"
          iconPosition="left"
          className="flex-1 min-w-0 px-1.5 sm:px-2"
          onClick={() => onDownload(file?.id)}
        >
          <span className="truncate">Télécharger</span>
        </Button>
        {canModifyFiles && (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            iconName="Trash2"
            className="shrink-0 h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
            aria-label="Supprimer le fichier"
            title="Supprimer"
            onClick={() => onDelete(file?.id)}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-foreground">Conception Fichiers</h2>
        <div className="flex gap-2">
          {showClinicalBoard && canBulkDownloadClinical && (
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              disabled={clinicalDownloadTargets.length === 0 || isBulkDownloading}
              loading={isBulkDownloading}
              onClick={() => void handleBulkDownloadClinical()}
            >
              Télécharger
            </Button>
          )}
        </div>
      </div>

      {caseData?.id && <CaseOrderServicesSummary caseId={caseData.id} />}

      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories?.map((category) => (
          <button
            key={category?.value}
            onClick={() => setSelectedCategory(category?.value)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              selectedCategory === category?.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name={category?.icon} size={16} />
            {category?.label}
          </button>
        ))}
      </div>

      {selectedCategory === 'prescription' ? (
        canEditPrescription && typeof onPrescriptionSave === 'function' ? (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Prescription modifiable pendant la soumission ou, en phase de révision, au jalon «
              contrôle qualité » si votre rôle y est autorisé.
            </p>
            <PrescriptionForm
              caseData={caseData}
              onDataChange={setPrescriptionData}
              allowPrescriptionEdit
            />
            <div className="flex justify-end">
              <Button
                type="button"
                variant="default"
                size="sm"
                iconName="Save"
                iconPosition="left"
                onClick={() => {
                  const base = formSnapshotFromCase(caseData);
                  const specs =
                    prescriptionData ?? buildTreatmentSpecsPayloadFromCase(caseData);
                  onPrescriptionSave({
                    ...base,
                    ...(prescriptionData
                      ? {
                          archToTreat:
                            prescriptionData.archToTreat ?? base.archToTreat,
                          treatmentType:
                            prescriptionData.treatmentType ?? base.treatmentType,
                        }
                      : {}),
                    treatmentSpecs: specs,
                  });
                }}
              >
                Enregistrer la prescription
              </Button>
            </div>
          </div>
        ) : (
          <CasePrescriptionReadView caseData={caseData} />
        )
      ) : (
        <>
          {showClinicalBoard && caseData?.id && patientId && (
            <CaseClinicalMediaBoard
              caseId={caseData.id}
              patientId={patientId}
              currentUserId={currentUserId}
              files={files}
              canModify={canModifyFiles}
              onFilesUpdated={onFilesUploaded}
              onDownload={onDownload}
              mode={boardMode}
            />
          )}

          {selectedCategory === 'all' && orphanFiles.length > 0 && (
            <div className="space-y-3 pt-2 border-t border-border">
              <h3 className="text-sm font-semibold text-foreground">Autres documents</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {orphanFiles.map(renderOrphanFileCard)}
              </div>
            </div>
          )}

          {selectedCategory === 'all' &&
            orphanFiles.length === 0 &&
            !files?.some(
              (f) =>
                f.category === 'scans' ||
                (f.category === 'photos' && f.photoKey) ||
                f.category === 'documents',
            ) && (
              <div className="text-center py-8">
                <Icon name="FolderOpen" size={40} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Utilisez les emplacements ci-dessus pour ajouter scans et photos.
                </p>
              </div>
            )}
        </>
      )}

      {previewFile && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewFile(null)}
        >
          <div
            className="max-w-4xl max-h-[90vh] bg-card rounded-lg overflow-hidden"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">{previewFile?.name}</h3>
              <Button
                variant="ghost"
                size="icon"
                iconName="X"
                onClick={() => setPreviewFile(null)}
              />
            </div>
            <div className="p-4">
              <Image src={previewFile?.url} alt={previewFile?.name} className="w-full h-auto" />
            </div>
          </div>
        </div>
      )}

      <StlViewerModal
        isOpen={stlViewerOpen}
        onClose={() => {
          setStlViewerOpen(false);
          setStlViewerFiles([]);
        }}
        stlFiles={stlViewerFiles}
      />

    </div>
  );
};

export default FilesTab;
